import React, { useCallback, useMemo } from "react";
import MobileBottomNavItem from "@/components/navigation/mobileBottomNavItem";
import { generatePath, useParams } from "react-router-dom";
import { navItemsConfig } from "@/components/navigation/config/navItemsConfig";
import { MOBILE_BOTTOM_NAV_KEYS } from "@/constants/navigationKeys";
import { buildPathWithLocale } from "@/utils/languageVersions/localeHelpers/pathWithLocale";
import { useScrollTopAvailability } from "@/hooks/useScrollTopAvailability";
import { Container, Nav } from "react-bootstrap";
import { useCartSummary } from "@/components/cart/hooks/useCartSummary";
import { getMenuTypeFromUrl } from "@/components/cart/helpers/cartEvents";
import { useGetMenuIdQuery } from "@/graphql/menuId/menuId.hooks";

interface IMobileBottomNavProps {
  onOpenSelected: () => void;
}

const MobileBottomNav: React.FC<IMobileBottomNavProps> = ({
  onOpenSelected,
}) => {
  const { locale, menuId, branchId, typeMenu } = useParams<{
    locale?: string;
    menuId?: string;
    branchId?: string;
    typeMenu?: string;
  }>();

  const menuTypeEnum = getMenuTypeFromUrl(typeMenu);
  const { data: menuIdData } = useGetMenuIdQuery({
    skip: !branchId || !menuTypeEnum,
    variables: {
      branchId: Number(branchId),
      typeMenu: menuTypeEnum,
    },
  });

  const menuData = menuId ? menuId : menuIdData?.menu?.id;
  const { hasItems } = useCartSummary(Number(menuData));
  const { isScrollTopEnabled, scrollToTop } = useScrollTopAvailability({
    activationScrollY: 120,
    scrollContainerId: "app-scroll-container",
  });

  const handleAction = useCallback(
    (actionId?: string) => {
      if (actionId === "scrollTop") {
        scrollToTop();
        return;
      }

      if (actionId === "selected") {
        onOpenSelected?.();
        return;
      }
    },
    [onOpenSelected, scrollToTop]
  );

  const items = useMemo(() => {
    return MOBILE_BOTTOM_NAV_KEYS.map((key) => navItemsConfig[key]);
  }, []);

  return (
    <Nav
      as="nav"
      className="nav-menu d-md-none position-fixed bottom-0 start-0 end-0 bg-white shadow-up"
      style={{ height: "var(--app-bottom-nav-height-mobile)" }}
    >
      <Container className="h-100">
        <div className="d-flex align-items-center justify-content-between h-100">
          {items.map((item) => {
            let path: string | undefined;

            if (item.type === "link") {
              const resolvedPath = (() => {
                if (!branchId || !typeMenu) return item.path;

                if (menuId && item.path) {
                  return generatePath(item.path, {
                    menuId,
                    branchId,
                    typeMenu,
                  });
                }

                if (!menuId && item.activePath) {
                  return generatePath(item.activePath, {
                    branchId,
                    typeMenu,
                  });
                }

                return item.path;
              })();

              path = buildPathWithLocale(locale ?? "", resolvedPath);
            }

            const isLinkDisabled =
              item.type === "link" && !branchId && !typeMenu;
            const isScrollTopItem =
              item.type === "action" && item.actionId === "scrollTop";

            const disabled = isScrollTopItem
              ? !isScrollTopEnabled
              : isLinkDisabled;

            return (
              <MobileBottomNavItem
                key={item.key}
                item={item}
                path={path}
                onAction={
                  item.type === "action"
                    ? () => handleAction(item.actionId)
                    : undefined
                }
                showDot={item.key === "selected" && hasItems}
                disabled={disabled}
              />
            );
          })}
        </div>
      </Container>
    </Nav>
  );
};

export default MobileBottomNav;
